import { HTMLAttributes, memo, ReactElement, useRef, useLayoutEffect, useState } from 'react';
import styled from '@emotion/styled';
import Slide, { SlideProps } from './Slide';
import StatusImage from './StatusImage';
import ProxyVideo from 'components/ProxyVideo';
import { Dropdown, Menu } from 'antd';
import copyText from 'utils/copyText';
import { useTranslate } from '@tolgee/react';

const HeightHolder = styled.span<{ width: string }>`
  flex: 0 0 0 !important;
  height: 0;
  margin-right: 0 !important;
  padding-bottom: ${({ width }) => width};
`;

export const ImageWrapper = styled.div<{ height: number }>`
  height: ${({ height }) => `${height}px`};
  background: #f3f3f3;
  > div {
    width: 100%;
    height: 100%;
    > * {
      width: 100%;
      height: 100%;
    }
  }
`;

const VideoWrapper = styled.div<{ height: number }>`
  height: ${({ height }) => `${height}px`};
  background: #f3f3f3;
  video {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

export type ImageRender = (
  src: string,
  props: HTMLAttributes<HTMLElement>,
  index: number,
) => ReactElement;

export type VideoRender = (src: string, index: number) => ReactElement;

const defaultVideoRender: VideoRender = (src) => {
  return (
    <ProxyVideo controls src={src}>
      <track kind="captions" />
    </ProxyVideo>
  );
};

export interface ImageSlideProps extends SlideProps {
  imageFirst?: boolean;
  images?: string[];
  videos?: string[];
  imageRender?: ImageRender;
  videoRender?: VideoRender;
  platform?: General.PlatformUpperCase;
}

function ImageSlide(props: ImageSlideProps) {
  const {
    count,
    offsetPercent,
    imageFirst = false,
    images = [],
    videos = [],
    imageRender,
    videoRender = defaultVideoRender,
    platform,
    ...otherProps
  } = props;

  const { t } = useTranslate();

  const widthPercent = (100 - (count - 1) * offsetPercent) / count;

  const heightHolderRef = useRef<HTMLElement>(null);

  const [height, setHeight] = useState(0);

  useLayoutEffect(() => {
    const { current: heightHolder } = heightHolderRef;

    if (heightHolder === null) {
      return;
    }

    const { height } = heightHolder.getBoundingClientRect();

    setHeight(height);
  }, []);

  const videoList = videos.map((src, index) => {
    return (
      <VideoWrapper key={src} height={height}>
        <Dropdown
          trigger={['contextMenu']}
          overlay={
            <Menu
              onClick={({ domEvent }) => {
                domEvent.stopPropagation();
                copyText(src);
              }}
            >
              <Menu.Item>{t('common.action.copyLink')}</Menu.Item>
            </Menu>
          }
        >
          {videoRender(src, index)}
        </Dropdown>
      </VideoWrapper>
    );
  });

  const imageList = images.map((src, index) => {
    return (
      <ImageWrapper key={src} height={height}>
        <Dropdown
          trigger={['contextMenu']}
          overlay={
            <Menu
              onClick={({ domEvent }) => {
                domEvent.stopPropagation();
                copyText(src);
              }}
            >
              <Menu.Item>{t('common.action.copyLink')}</Menu.Item>
            </Menu>
          }
        >
          <div>
            <StatusImage
              src={src}
              platform={platform}
              imageRender={imageRender ? (src, props) => imageRender(src, props, index) : undefined}
            />
          </div>
        </Dropdown>
      </ImageWrapper>
    );
  });

  return (
    <Slide count={count} offsetPercent={offsetPercent} {...otherProps}>
      <HeightHolder ref={heightHolderRef} width={`${widthPercent}%`} />
      {imageFirst ? [imageList, videoList] : [videoList, imageList]}
    </Slide>
  );
}

export default memo(ImageSlide);
